import React, { useMemo, useState, useCallback, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { computeForecastValues } from './forecastUtils';
import { getFieldLabel } from '@/lib/fieldLabel';
import { periodBadges } from '@/lib/periodBadges';

const ROW_HEIGHT = 32; // Approximate row height in pixels
const OVERSCAN = 10; // Extra rows to render above/below viewport

// Print-friendly font stacks selectable per-table via vizConfig.fontFamily.
const FONT_STACKS = {
  arial: 'Arial, Helvetica, sans-serif',
  helvetica: '"Helvetica Neue", Helvetica, Arial, sans-serif',
  times: '"Times New Roman", Times, serif',
  georgia: 'Georgia, "Times New Roman", serif',
  verdana: 'Verdana, Geneva, sans-serif',
  courier: '"Courier New", Courier, monospace',
};

// Popover rendered in a portal so it escapes the visual card's `overflow-hidden`
// + transformed (react-grid-layout) ancestor, which would otherwise clip it.
// Auto-flips/clamps to stay fully inside the viewport.
function TablePopover({ anchor, width, onClose, testid, children }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ left: anchor.x, top: anchor.y, opacity: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const w = el.offsetWidth || width;
    const h = el.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = anchor.x;
    let top = anchor.y;
    if (left + w + 8 > vw) left = vw - w - 8;
    if (left < 8) left = 8;
    if (top + h + 8 > vh) {
      const above = (anchor.flipY != null ? anchor.flipY : anchor.y) - h - 4;
      top = above >= 8 ? above : Math.max(8, vh - h - 8);
    }
    if (top < 8) top = 8;
    setPos({ left, top, opacity: 1 });
  }, [anchor, width]);
  return createPortal(
    <>
      <div className="fixed inset-0 z-[9998]" onClick={onClose} data-testid={`${testid}-backdrop`} onContextMenu={(e) => { e.preventDefault(); onClose(); }} />
      <div
        ref={ref}
        data-testid={testid}
        onClick={(e) => e.stopPropagation()}
        style={{ position: 'fixed', zIndex: 9999, width, left: pos.left, top: pos.top, opacity: pos.opacity }}
      >
        {children}
      </div>
    </>,
    document.body
  );
}

// Conditional formatting evaluator
function evalCondFormat(value, rules, allValues) {
  if (!rules || rules.length === 0 || value === null || value === undefined) return {};
  const numVal = typeof value === 'number' ? value : parseFloat(value);
  if (isNaN(numVal)) return {};
  let style = {};
  let icon = null;
  let barWidth = null;
  for (const rule of rules) {
    if (rule.type === 'threshold') {
      const ops = { gt: numVal > rule.value, lt: numVal < rule.value, gte: numVal >= rule.value, lte: numVal <= rule.value, eq: numVal === rule.value, neq: numVal !== rule.value };
      if (ops[rule.operator]) {
        if (rule.bgColor) style.backgroundColor = rule.bgColor;
        if (rule.textColor) style.color = rule.textColor;
        if (rule.bold) style.fontWeight = 700;
      }
    } else if (rule.type === 'color_scale' && allValues.length > 0) {
      const min = Math.min(...allValues);
      const max = Math.max(...allValues);
      const pct = (numVal - min) / (max - min || 1);
      const c1 = rule.colors?.[0] || [76, 175, 80];
      const c2 = rule.colors?.[1] || [255, 235, 59];
      const c3 = rule.colors?.[2] || [244, 67, 54];
      let r, g, b;
      if (pct <= 0.5) { const p = pct * 2; r = c1[0] + (c2[0]-c1[0])*p; g = c1[1] + (c2[1]-c1[1])*p; b = c1[2] + (c2[2]-c1[2])*p; }
      else { const p = (pct-0.5)*2; r = c2[0] + (c3[0]-c2[0])*p; g = c2[1] + (c3[1]-c2[1])*p; b = c2[2] + (c3[2]-c2[2])*p; }
      style.backgroundColor = `rgba(${Math.round(r)},${Math.round(g)},${Math.round(b)},0.2)`;
      style.color = `rgb(${Math.round(r)},${Math.round(g)},${Math.round(b)})`;
    } else if (rule.type === 'data_bar' && allValues.length > 0) {
      const max = Math.max(...allValues.map(Math.abs));
      barWidth = max > 0 ? Math.max(0, Math.min(100, (Math.abs(numVal) / max) * 100)) : 0;
      style._barColor = numVal >= 0 ? (rule.positiveColor || 'rgba(59,130,246,0.3)') : (rule.negativeColor || 'rgba(239,68,68,0.3)');
    } else if (rule.type === 'icon_set') {
      const t = rule.thresholds || [33, 66];
      if (allValues.length > 0) {
        const min = Math.min(...allValues);
        const max = Math.max(...allValues);
        const pct = ((numVal - min) / (max - min || 1)) * 100;
        const icons = rule.icons || ['▼', '●', '▲'];
        const colors = rule.iconColors || ['#f44336', '#ff9800', '#4caf50'];
        const idx = pct >= t[1] ? 2 : pct >= t[0] ? 1 : 0;
        icon = icons[idx];
        style._iconColor = colors[idx];
      }
    }
  }
  return { style, icon, barWidth };
}

export const TableRenderer = React.memo(function TableRenderer({ result, vizConfig, formatValue, allFields, fieldOverrides, rowDims, onDrill, printMode, onVizConfigChange, rowTrends, rowTrendLabels, rowTrendTarget, onAnomalyClick, onRowContextMenu }) {
  const cfg = useMemo(() => vizConfig || {}, [vizConfig]);
  
  // All hooks must be declared before any early return
  const [dragCol, setDragCol] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const scrollRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(400);
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  // Column resize — live width while dragging, committed to cfg.columnWidths on release.
  const [liveWidths, setLiveWidths] = useState(null);
  const resizeRef = useRef(null);
  // Right-click column header settings popover: { col, x, y }
  const [headerMenu, setHeaderMenu] = useState(null);
  const [applyAllCols, setApplyAllCols] = useState(false);
  const [rowMenu, setRowMenu] = useState(null);
  const [autoFontPx, setAutoFontPx] = useState(null);
  const updateCfg = useCallback((patch) => { if (onVizConfigChange) onVizConfigChange({ ...(vizConfig || {}), ...patch }); }, [vizConfig, onVizConfigChange]);
  // Auto-fit font: shrink the table font so all columns fit the available width
  // (no horizontal scroll). Stable — computes the natural width AT BASE font.
  useEffect(() => {
    const autoFitOn = (vizConfig || {}).autoFitFont !== false;
    if (!autoFitOn) { setAutoFontPx(null); return; }
    const el = scrollRef.current;
    if (!el) return;
    const base = (vizConfig || {}).fontSize ? Number((vizConfig || {}).fontSize) : 12;
    const recalc = () => {
      const tbl = el.querySelector('table');
      if (!tbl) return;
      const avail = el.clientWidth;
      const natural = tbl.scrollWidth;
      if (!avail || !natural) return;
      setAutoFontPx(prev => {
        const current = prev || base;
        const naturalAtBase = natural * (base / current);
        const desired = naturalAtBase > avail ? Math.max(7, Math.floor(base * avail / naturalAtBase)) : null;
        return desired === prev ? prev : desired;
      });
    };
    recalc();
    const ro = new ResizeObserver(recalc);
    ro.observe(el);
    return () => ro.disconnect();
  }, [vizConfig, result]);
  // Scoped id so per-table font overrides don't leak to sibling tables.
  const fontScopeId = 'tbl-font-' + React.useId().replace(/[^a-zA-Z0-9]/g, '');
  
  // Virtual scrolling effect — must be before early return
  const rowCount = result?.data?.length || 0;
  const useVirtual = !printMode && rowCount > 100;
  useEffect(() => {
    if (!useVirtual || !scrollRef.current) return;
    const el = scrollRef.current;
    setContainerHeight(el.clientHeight);
    const handleScroll = () => setScrollTop(el.scrollTop);
    el.addEventListener('scroll', handleScroll, { passive: true });
    const ro = new ResizeObserver(() => setContainerHeight(el.clientHeight));
    ro.observe(el);
    return () => { el.removeEventListener('scroll', handleScroll); ro.disconnect(); };
  }, [useVirtual, rowCount]);

  // Pre-compute column values for conditional formatting (must be before early return)
  const condFormatsEarly = useMemo(() => (vizConfig || {}).conditionalFormatting || {}, [vizConfig]);
  const colValuesEarly = useMemo(() => {
    const data = result?.data || [];
    const cv = {};
    Object.keys(condFormatsEarly).forEach(k => {
      cv[k] = data.map(r => typeof r[k] === 'number' ? r[k] : 0).filter(v => !isNaN(v));
    });
    return cv;
  }, [result?.data, condFormatsEarly]);

  const handleDragStart = useCallback((e, colKey) => {
    setDragCol(colKey);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', colKey);
    e.target.style.opacity = '0.5';
  }, []);

  const handleDragEnd = useCallback((e) => {
    e.target.style.opacity = '1';
    setDragCol(null);
    setDropTarget(null);
  }, []);

  const handleDragOver = useCallback((e, colKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (colKey !== dragCol) setDropTarget(colKey);
  }, [dragCol]);

  // Compute raw keys and ordered keys via useMemo (must be before early return)
  const rawKeys = useMemo(() => {
    if (!result?.data?.length) return [];
    return Object.keys(result.data[0]).filter(k => !k.startsWith('_sort_') && !k.startsWith('_isForecast') && k !== '_muted' && !k.startsWith('_anomaly') && !k.startsWith('_suppress') && !k.endsWith('__prior') && !k.endsWith('__prior_pct'));
  }, [result?.data]);

  const keys = useMemo(() => {
    const savedOrder = cfg.columnOrder;
    if (!savedOrder || !Array.isArray(savedOrder) || savedOrder.length === 0) return rawKeys;
    const ordered = savedOrder.filter(k => rawKeys.includes(k));
    const remaining = rawKeys.filter(k => !savedOrder.includes(k));
    return [...ordered, ...remaining];
  }, [rawKeys, cfg.columnOrder]);

  const handleDrop = useCallback((e, targetCol) => {
    e.preventDefault();
    const sourceCol = dragCol;
    if (!sourceCol || sourceCol === targetCol) { setDragCol(null); setDropTarget(null); return; }
    
    const newOrder = [...keys];
    const srcIdx = newOrder.indexOf(sourceCol);
    const tgtIdx = newOrder.indexOf(targetCol);
    if (srcIdx === -1 || tgtIdx === -1) return;
    
    newOrder.splice(srcIdx, 1);
    newOrder.splice(tgtIdx, 0, sourceCol);
    
    setDragCol(null);
    setDropTarget(null);
    
    if (onVizConfigChange) {
      onVizConfigChange({ ...cfg, columnOrder: newOrder });
    }
  }, [dragCol, keys, cfg, onVizConfigChange]);

  // ── Column resize ──────────────────────────────────────────────────────────
  // Drag the handle on a column's right edge to resize; the width persists into
  // vizConfig.columnWidths so it survives save/reload in the dashboard designer.
  const startResize = useCallback((e, col) => {
    e.preventDefault();
    e.stopPropagation();
    const th = e.currentTarget.closest('th');
    const startX = e.clientX;
    const startW = th ? th.offsetWidth : 120;
    const base = { ...((cfg.columnWidths) || {}) };
    resizeRef.current = { col, startX, startW, base };
    const onMove = (ev) => {
      const r = resizeRef.current;
      if (!r) return;
      const w = Math.max(48, Math.round(r.startW + (ev.clientX - r.startX)));
      setLiveWidths({ ...r.base, [r.col]: w });
    };
    const onUp = () => {
      const r = resizeRef.current;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      resizeRef.current = null;
      setLiveWidths((lw) => {
        if (lw && onVizConfigChange) onVizConfigChange({ ...cfg, columnWidths: lw });
        return null;
      });
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [cfg, onVizConfigChange]);

  // Early return after all hooks
  if (!result?.data?.length) return null;
  const maxRows = printMode ? 10000 : (cfg.maxRows || 1000);
  let data = result.data.slice(0, maxRows);
  const totalRows = result.data.length;

  // Sort by dimensions using _sort_ columns (same logic as ChartRenderer)
  // Month name lookup for intelligent sorting
  const MONTH_ORDER = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
    january: 1, february: 2, march: 3, april: 4, june: 6, july: 7, august: 8, september: 9, october: 10, november: 11, december: 12 };
  const DAY_ORDER = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6,
    sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };

  const sortByMap = {};
  allFields.forEach(f => { if (f.sort_by_field) sortByMap[f.name] = f.sort_by_field; });
  if (data[0]) {
    for (const key of Object.keys(data[0])) {
      if (key.startsWith('_sort_')) {
        const fieldName = key.substring(6);
        if (!sortByMap[fieldName]) sortByMap[fieldName] = key;
      }
    }
  }

  const getSortValue = (row, dim) => {
    const sortKey = sortByMap[dim];
    if (sortKey && row[sortKey] !== undefined) return row[sortKey];
    if (row['_sort_' + dim] !== undefined) return row['_sort_' + dim];
    const val = row[dim];
    if (val == null) return null;
    const lower = String(val).toLowerCase();
    if (MONTH_ORDER[lower] !== undefined) return MONTH_ORDER[lower];
    if (DAY_ORDER[lower] !== undefined) return DAY_ORDER[lower];
    return val;
  };

  if (rowDims?.length > 0) {
    data = [...data].sort((a, b) => {
      for (const dim of rowDims) {
        const aVal = getSortValue(a, dim);
        const bVal = getSortValue(b, dim);
        if (aVal === bVal) continue;
        if (aVal == null) return 1;
        if (bVal == null) return -1;
        if (typeof aVal === 'number' && typeof bVal === 'number') return aVal - bVal;
        return String(aVal).localeCompare(String(bVal));
      }
      return 0;
    });
  }

  const getColHeader = (k) => { const cl = (cfg.columnLabels || {})[k]; if (cl) return cl; const ovr = fieldOverrides[k]; const f = allFields.find(ff => ff.name === k); return getFieldLabel(f, k, ovr); };
  const compact = cfg.compact;
  const cellPad = compact ? 'px-2 py-1' : 'px-3 py-2';
  const freeze = cfg.freezeHeader !== false;
  const totalsPosition = cfg.totalsPosition || 'bottom';
  // Customizable subtotal / grand-total row labels. The subtotal label supports
  // a {group} placeholder that is replaced with the current group's name.
  const grandTotalLabel = cfg.grandTotalLabel ?? 'Grand Total';
  const subtotalLabelFor = (group) => {
    const tmpl = cfg.subtotalLabel ?? 'Subtotal';
    return /\{group\}/.test(tmpl) ? tmpl.replace(/\{group\}/g, group ?? '') : tmpl;
  };

  // Identify numeric columns — check actual data values since allFields may be incomplete
  const numericCols = keys.filter(k => {
    // Skip dimension fields
    if (rowDims.includes(k)) return false;
    // Check allFields metadata
    const f = allFields.find(ff => ff.name === k);
    if (f && f.semantic_type === 'measure') return true;
    if (f && f.semantic_type === 'dimension') return false;
    // Fallback: check if majority of values are numbers
    const numCount = data.filter(row => typeof row[k] === 'number').length;
    return numCount > data.length * 0.5;
  });

  // Optional sparkline column — renders a tiny inline trend per row using
  // all the numeric columns in display order. Useful when the table is
  // already pivoted across time (Year 2020 / 2021 / 2022 / ...) so the
  // sparkline becomes a "row-level trend at a glance" without adding any
  // extra data plumbing. Activated via `vizConfig.sparklineColumn = true`.
  const SPARK_KEY = '__sparkline__';
  // A caller can supply an explicit per-row time series (e.g. AI Explore's
  // "Trend" toggle fetches each dimension value's metric over time). That takes
  // precedence over the derive-from-row-columns sparkline.
  const hasRowTrends = rowTrends && Object.keys(rowTrends).length > 0;
  const showSpark = hasRowTrends || (cfg.sparklineColumn === true && numericCols.length >= 3);
  const displayKeys = showSpark ? [...keys, SPARK_KEY] : keys;

  // Forecasting: append forecast rows to data
  if (cfg.forecast && numericCols.length > 0 && data.length >= 2) {
    const periods = cfg.forecastPeriods || 3;
    const method = cfg.forecastMethod || 'linear';
    const xKey = rowDims[0] || keys[0];
    const forecastRows = [];
    for (let p = 0; p < periods; p++) {
      const fcRow = { [xKey]: `Forecast ${p + 1}`, _isForecast: true };
      // Copy non-measure dimensions as empty
      keys.forEach(k => { if (!numericCols.includes(k) && k !== xKey) fcRow[k] = ''; });
      numericCols.forEach(measureKey => {
        const vals = data.map(d => typeof d[measureKey] === 'number' ? d[measureKey] : 0);
        const fcVals = computeForecastValues(vals, method, periods, cfg.forecastSeasonLength || 12);
        fcRow[measureKey] = fcVals[p] !== undefined ? fcVals[p] : null;
      });
      forecastRows.push(fcRow);
    }
    data = [...data, ...forecastRows];
  }

  // Compute grand totals (exclude forecast rows)
  const grandTotals = {};
  const actualData = data.filter(r => !r._isForecast);
  if (cfg.grandTotals && numericCols.length > 0) {
    numericCols.forEach(k => {
      grandTotals[k] = actualData.reduce((sum, row) => sum + (typeof row[k] === 'number' ? row[k] : 0), 0);
    });
  }

  // Compute subtotals by first dimension (exclude forecast rows)
  const groupByDims = cfg.groupBy || (cfg.subtotals && rowDims.length >= 2 ? [rowDims[0]] : []);
  const hasGrouping = groupByDims.length > 0;
  
  let subtotalMap = {};
  let groupCounts = {};
  if (hasGrouping) {
    actualData.forEach(row => {
      const groupKey = groupByDims.map(d => String(row[d] ?? '')).join('|||');
      if (!subtotalMap[groupKey]) { subtotalMap[groupKey] = {}; groupCounts[groupKey] = 0; }
      groupCounts[groupKey]++;
      numericCols.forEach(k => {
        subtotalMap[groupKey][k] = (subtotalMap[groupKey][k] || 0) + (typeof row[k] === 'number' ? row[k] : 0);
      });
    });
    // Compute averages
    Object.keys(subtotalMap).forEach(gk => {
      subtotalMap[gk]._count = groupCounts[gk];
      numericCols.forEach(k => {
        subtotalMap[gk][`${k}_avg`] = groupCounts[gk] > 0 ? subtotalMap[gk][k] / groupCounts[gk] : 0;
      });
    });
  }

  // Build rows with group headers, data, and group footers
  const renderRows = [];
  let lastGroupKey = null;
  data.forEach((row, i) => {
    const groupKey = hasGrouping ? groupByDims.map(d => String(row[d] ?? '')).join('|||') : null;
    
    if (hasGrouping && groupKey !== lastGroupKey) {
      // Close previous group with footer
      if (lastGroupKey !== null && cfg.groupFooter !== false) {
        renderRows.push({ type: 'group_footer', groupKey: lastGroupKey, totals: subtotalMap[lastGroupKey] || {}, groupLabels: lastGroupKey.split('|||') });
      }
      // Open new group with header
      const groupLabels = groupKey.split('|||');
      const count = groupCounts[groupKey] || 0;
      renderRows.push({ type: 'group_header', groupKey, groupLabels, count, pageBreak: cfg.groupPageBreak && lastGroupKey !== null });
      lastGroupKey = groupKey;
    }
    
    // Only add data row if group is not collapsed
    if (!hasGrouping || !collapsedGroups.has(groupKey)) {
      renderRows.push({ type: 'data', row, index: i });
    }
  });
  // Close last group
  if (hasGrouping && lastGroupKey !== null && cfg.groupFooter !== false) {
    renderRows.push({ type: 'group_footer', groupKey: lastGroupKey, totals: subtotalMap[lastGroupKey] || {}, groupLabels: lastGroupKey.split('|||') });
  }

  // Excel-style merged cells for repeating dimension values: blank a dimension
  // cell when it matches the value directly above it. Comparison resets at any
  // non-data row (group header/footer) so groups never merge across boundaries.
  if (cfg.mergeRepeatedCells) {
    let prev = null;
    renderRows.forEach(item => {
      if (item.type !== 'data') { prev = null; return; }
      const blank = {};
      let stillSame = prev !== null;
      rowDims.forEach(k => {
        // Once a higher-priority dim changes, lower dims must show again.
        if (stillSame && String(item.row[k] ?? '') === String(prev.row[k] ?? '')) {
          blank[k] = true;
        } else {
          stillSame = false;
        }
      });
      item.mergeBlank = blank;
      prev = item;
    });
  }

  const GrandTotalRow = () => (
    <tr className="border-t-2 border-primary bg-[#e8f0fe] dark:bg-primary/15 font-bold">
      {cfg.rowNumbers && <td className={cellPad}></td>}
      {displayKeys.map((k, i) => (
        <td key={k} className={cellPad + ' font-mono text-foreground'} style={{ textAlign: getColAlign(k) }}>
          {i === 0 ? grandTotalLabel : numericCols.includes(k) ? formatValue(grandTotals[k], k) : ''}
        </td>
      ))}
    </tr>
  );

  const colAlignments = cfg.columnAlignments || {};
  const colWidths = liveWidths || cfg.columnWidths || {};
  const condFormats = cfg.conditionalFormatting || {};
  const getColAlign = (k) => colAlignments[k] || (numericCols.includes(k) ? 'right' : 'left');
  const getColWidth = (k) => colWidths[k] ? `${colWidths[k]}px` : undefined;
  const colValues = colValuesEarly;

  // Per-table font family / size. Applied via a scoped <style> so it overrides
  // the cells' `font-mono` class and carries into print/PDF output too.
  const tableFontFamily = cfg.fontFamily && cfg.fontFamily !== 'default' ? FONT_STACKS[cfg.fontFamily] : null;
  const tableFontSize = cfg.fontSize ? Number(cfg.fontSize) : null;
  // Column header overrides + wrap/auto-fit behaviour (default ON).
  const headerFonts = cfg.columnHeaderFonts || {};
  const autoWrap = cfg.autoWrapText !== false;
  const autoFit = cfg.autoFitFont !== false;
  const wrapClass = autoWrap ? ' whitespace-normal break-words [overflow-wrap:anywhere]' : ' whitespace-nowrap overflow-hidden text-ellipsis';
  const effectiveFontSize = autoFontPx || tableFontSize;
  const bodyBold = cfg.bodyBold === true;
  const scopeSel = `[data-table-font="${fontScopeId}"]`;
  const tdFontRules = `${tableFontFamily ? `font-family:${tableFontFamily} !important;` : ''}${effectiveFontSize ? `font-size:${effectiveFontSize}px !important;` : ''}${bodyBold ? 'font-weight:700 !important;' : ''}`;
  // The table uses `border-separate` (see <table> below) so sticky header cells
  // paint an opaque background correctly. `border-separate` drops <tr> borders,
  // so re-create the row separators on the cells here.
  const scopedCss = [
    tdFontRules ? `${scopeSel} td{${tdFontRules}}` : '',
    `${scopeSel} tbody td{border-bottom:1px solid hsl(var(--border) / 0.2);}`,
    `${scopeSel} thead th{border-bottom:1px solid hsl(var(--border) / 0.5);}`,
    `${scopeSel} tfoot td{border-top:2px solid hsl(var(--primary) / 0.6);}`,
  ].filter(Boolean).join('');

  // Virtual scrolling calculations
  const totalHeight = renderRows.length * ROW_HEIGHT;

  const startIdx = useVirtual ? Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN) : 0;
  const endIdx = useVirtual ? Math.min(renderRows.length, Math.ceil((scrollTop + containerHeight) / ROW_HEIGHT) + OVERSCAN) : renderRows.length;
  const visibleRows = renderRows.slice(startIdx, endIdx);
  const topPad = startIdx * ROW_HEIGHT;
  const bottomPad = (renderRows.length - endIdx) * ROW_HEIGHT;

  return (
    <div data-testid="table-container" data-table-font={fontScopeId} className={printMode ? 'relative' : 'relative h-full flex flex-col min-h-0'}>
      {scopedCss && <style>{scopedCss}</style>}
      {headerMenu && (() => {
        const col = headerMenu.col;
        const hf = headerFonts[col] || {};
        const size = hf.size || 10;
        const curLabel = (cfg.columnLabels || {})[col] ?? getColHeader(col);
        const setHF = (patch) => {
          if (applyAllCols) {
            const next = { ...headerFonts };
            displayKeys.forEach(k => { if (k !== SPARK_KEY) next[k] = { ...(headerFonts[k] || {}), ...patch }; });
            updateCfg({ columnHeaderFonts: next });
          } else {
            updateCfg({ columnHeaderFonts: { ...headerFonts, [col]: { ...hf, ...patch } } });
          }
        };
        return (
          <TablePopover anchor={headerMenu} width={256} onClose={() => setHeaderMenu(null)} testid="col-header-menu">
            <div className="rounded-xl border border-border/60 bg-white dark:bg-[#15151c] shadow-xl p-3 text-xs">
              <div className="font-heading font-semibold text-foreground mb-2">Column header</div>
              <label className="block text-[10px] text-muted-foreground mb-1">Label</label>
              <input autoFocus value={curLabel} onChange={e => updateCfg({ columnLabels: { ...(cfg.columnLabels || {}), [col]: e.target.value } })} className="w-full px-2 py-1 rounded-md bg-muted/50 border border-border/50 text-foreground mb-3" data-testid="col-header-label-input" />
              <label className="block text-[10px] text-muted-foreground mb-1">Header font size</label>
              <div className="flex items-center gap-1 mb-3">
                <button type="button" className="px-2 py-1 rounded-md border border-border/50 hover:bg-muted/50" onClick={() => setHF({ size: Math.max(7, size - 1) })} data-testid="col-header-font-dec">A&minus;</button>
                <span className="w-12 text-center tabular-nums text-foreground" data-testid="col-header-font-size">{size}px</span>
                <button type="button" className="px-2 py-1 rounded-md border border-border/50 hover:bg-muted/50" onClick={() => setHF({ size: Math.min(28, size + 1) })} data-testid="col-header-font-inc">A+</button>
                <button type="button" className={'ml-1 px-2.5 py-1 rounded-md border font-bold ' + (hf.bold ? 'border-primary bg-primary/10 text-primary' : 'border-border/50 hover:bg-muted/50')} onClick={() => setHF({ bold: !hf.bold })} data-testid="col-header-bold">B</button>
              </div>
              <div className="border-t border-border/40 pt-2 space-y-2">
                <label className="flex items-center gap-2 cursor-pointer text-foreground" data-testid="col-apply-all-toggle"><input type="checkbox" checked={applyAllCols} onChange={e => setApplyAllCols(e.target.checked)} className="accent-primary" /> Apply font changes to all columns</label>
                <label className="flex items-center gap-2 cursor-pointer text-foreground" data-testid="col-autowrap-toggle"><input type="checkbox" checked={autoWrap} onChange={e => updateCfg({ autoWrapText: e.target.checked })} className="accent-primary" /> Auto-wrap text</label>
                <label className="flex items-center gap-2 cursor-pointer text-foreground" data-testid="col-autofit-toggle"><input type="checkbox" checked={autoFit} onChange={e => updateCfg({ autoFitFont: e.target.checked })} className="accent-primary" /> Auto-fit font to width</label>
              </div>
              <div className="flex items-center justify-between mt-3">
                <button type="button" className="text-[10px] text-muted-foreground hover:text-destructive" onClick={() => { if (applyAllCols) { updateCfg({ columnHeaderFonts: {} }); } else { const nl = { ...(cfg.columnLabels || {}) }; delete nl[col]; const nf = { ...headerFonts }; delete nf[col]; updateCfg({ columnLabels: nl, columnHeaderFonts: nf }); } }} data-testid="col-header-reset">{applyAllCols ? 'Reset all' : 'Reset column'}</button>
                <button type="button" className="px-3 py-1 rounded-md bg-primary text-primary-foreground text-[11px]" onClick={() => setHeaderMenu(null)} data-testid="col-header-done">Done</button>
              </div>
            </div>
          </TablePopover>
        );
      })()}
      {rowMenu && (() => {
        const fam = cfg.fontFamily || 'default';
        const size = cfg.fontSize ? Number(cfg.fontSize) : 12;
        const bold = cfg.bodyBold === true;
        return (
          <TablePopover anchor={rowMenu} width={224} onClose={() => setRowMenu(null)} testid="row-text-menu">
            <div className="rounded-xl border border-border/60 bg-white dark:bg-[#15151c] shadow-xl p-3 text-xs">
              <div className="font-heading font-semibold text-foreground mb-2">Row text</div>
              <label className="block text-[10px] text-muted-foreground mb-1">Font</label>
              <select value={fam} onChange={e => updateCfg({ fontFamily: e.target.value })} className="w-full px-2 py-1 rounded-md bg-muted/50 border border-border/50 text-foreground mb-3" data-testid="row-font-family">
                <option value="default">Default</option>
                <option value="arial">Arial</option>
                <option value="helvetica">Helvetica</option>
                <option value="times">Times New Roman</option>
                <option value="georgia">Georgia</option>
                <option value="verdana">Verdana</option>
                <option value="courier">Courier New</option>
              </select>
              <label className="block text-[10px] text-muted-foreground mb-1">Font size</label>
              <div className="flex items-center gap-1 mb-2">
                <button type="button" className="px-2 py-1 rounded-md border border-border/50 hover:bg-muted/50" onClick={() => updateCfg({ fontSize: Math.max(7, size - 1) })} data-testid="row-font-dec">A&minus;</button>
                <span className="w-12 text-center tabular-nums text-foreground" data-testid="row-font-size">{size}px</span>
                <button type="button" className="px-2 py-1 rounded-md border border-border/50 hover:bg-muted/50" onClick={() => updateCfg({ fontSize: Math.min(28, size + 1) })} data-testid="row-font-inc">A+</button>
                <button type="button" className={'ml-1 px-2.5 py-1 rounded-md border font-bold ' + (bold ? 'border-primary bg-primary/10 text-primary' : 'border-border/50 hover:bg-muted/50')} onClick={() => updateCfg({ bodyBold: !bold })} data-testid="row-bold">B</button>
              </div>
              <div className="flex items-center justify-between mt-3">
                <button type="button" className="text-[10px] text-muted-foreground hover:text-destructive" onClick={() => updateCfg({ fontFamily: 'default', bodyBold: false })} data-testid="row-text-reset">Reset</button>
                <button type="button" className="px-3 py-1 rounded-md bg-primary text-primary-foreground text-[11px]" onClick={() => setRowMenu(null)} data-testid="row-text-done">Done</button>
              </div>
            </div>
          </TablePopover>
        );
      })()}
      {/* Inline toolbar — surfaces feature toggles that the column-format
          panel hides behind 4 clicks. Visible only when not printing AND
          when the toggle would actually do something. */}
      {!printMode && numericCols.length >= 3 && typeof onVizConfigChange === 'function' && (
        <div className="flex items-center justify-end gap-2 mb-1.5 px-1" data-testid="table-toolbar">
          <button
            type="button"
            onClick={() => onVizConfigChange({ ...(cfg || {}), sparklineColumn: !showSpark })}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10px] font-medium transition-colors ${
              showSpark
                ? 'border-cyan-500/40 bg-cyan-500/10 text-cyan-300'
                : 'border-border/40 text-muted-foreground hover:text-foreground hover:border-border/70'
            }`}
            title={showSpark ? 'Hide the inline trend column' : 'Add an inline trend sparkline for each row'}
            data-testid="table-spark-toggle"
          >
            {/* Tiny trend glyph rendered as inline SVG — keeps the toggle
                self-describing without pulling in a new icon import. */}
            <svg width="20" height="10" viewBox="0 0 20 10" aria-hidden="true">
              <path d="M0,8 L4,5 L8,7 L12,3 L16,5 L20,1" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>Trend</span>
          </button>
        </div>
      )}
      <div ref={scrollRef} className={printMode ? 'rounded-lg' : 'overflow-auto rounded-lg flex-1 min-h-0'} data-testid="table-scroll-container" style={printMode ? { overflow: 'visible' } : { maxHeight: '100%' }}>
      <table className="w-full text-xs border-separate" style={{ borderSpacing: 0, tableLayout: Object.keys(colWidths).length > 0 ? 'fixed' : 'auto', ...(printMode ? { pageBreakInside: 'auto' } : {}) }}>
        {Object.keys(colWidths).length > 0 && (
          <colgroup>
            {cfg.rowNumbers && <col style={{ width: '40px' }} />}
            {displayKeys.map(k => <col key={k} style={{ width: getColWidth(k) }} />)}
          </colgroup>
        )}
        <thead className={freeze && !printMode ? 'sticky top-0 z-20 bg-white dark:bg-[#0b0b0d]' : ''} style={printMode ? { display: 'table-header-group' } : {}}>
          <tr className="border-b border-border/50">
            {cfg.rowNumbers && <th className={cellPad + ' text-left text-[10px] font-heading font-semibold text-muted-foreground bg-white dark:bg-[#0b0b0d]'} style={{ width: '40px' }}>#</th>}
            {displayKeys.map(c => (
              <th
                key={c}
                draggable={!printMode && c !== SPARK_KEY}
                onDragStart={e => handleDragStart(e, c)}
                onDragEnd={handleDragEnd}
                onDragOver={e => handleDragOver(e, c)}
                onDragLeave={() => setDropTarget(null)}
                onDrop={e => handleDrop(e, c)}
                onContextMenuCapture={!printMode && c !== SPARK_KEY && onVizConfigChange ? (e) => { e.preventDefault(); e.stopPropagation(); if (e.nativeEvent && e.nativeEvent.stopImmediatePropagation) e.nativeEvent.stopImmediatePropagation(); const r = e.currentTarget.getBoundingClientRect(); setRowMenu(null); setHeaderMenu({ col: c, x: r.left, y: r.bottom + 4, flipY: r.top - 4 }); } : undefined}
                className={cellPad + ' relative text-[10px] font-heading font-semibold text-muted-foreground tracking-wide bg-white dark:bg-[#0b0b0d] select-none' + (!printMode && c !== SPARK_KEY ? ' cursor-grab active:cursor-grabbing' : '') + (dropTarget === c ? ' border-l-2 border-l-primary' : '')}
                style={{ textAlign: c === SPARK_KEY ? 'center' : getColAlign(c), width: c === SPARK_KEY ? 90 : getColWidth(c), opacity: dragCol === c ? 0.4 : 1, ...(headerFonts[c] ? { fontSize: headerFonts[c].size ? headerFonts[c].size + 'px' : undefined, fontWeight: headerFonts[c].bold ? 700 : undefined } : {}) }}
                title={!printMode && c !== SPARK_KEY ? 'Right-click to rename or restyle this header' : undefined}
                data-testid={`table-col-header-${c}`}
              >
                <span className={'flex items-center gap-1' + wrapClass} style={{ justifyContent: c === SPARK_KEY ? 'center' : (getColAlign(c) === 'right' ? 'flex-end' : 'flex-start') }}>
                  {c === SPARK_KEY ? 'Trend' : (<>{!printMode && <span className="text-muted-foreground/30 text-[8px]">{'\u2630'}</span>}{getColHeader(c)}{periodBadges(allFields.find(ff => ff.name === c)?.formula).map(b => (
                    <span key={b} className={`ml-0.5 px-1 py-px rounded text-[7px] font-semibold leading-none normal-case ${b.startsWith('vs') ? 'bg-amber-500/15 text-amber-500' : 'bg-primary/15 text-primary'}`} data-testid={`col-period-badge-${c}-${b.replace(/\s+/g, '-')}`}>{b}</span>
                  ))}</>)}
                </span>
                {!printMode && c !== SPARK_KEY && (
                  <span
                    onMouseDown={e => startResize(e, c)}
                    onDragStart={e => { e.preventDefault(); e.stopPropagation(); }}
                    onClick={e => e.stopPropagation()}
                    draggable={false}
                    className="group/resize absolute top-0 -right-1.5 h-full w-3 z-20 flex justify-center cursor-col-resize"
                    style={{ touchAction: 'none' }}
                    title="Drag to resize column"
                    data-testid={`col-resize-${c}`}
                  >
                    <span className="w-px h-full bg-border/60 group-hover/resize:w-0.5 group-hover/resize:bg-primary transition-all" />
                  </span>
                )}
              </th>
            ))}
          </tr>
          {cfg.grandTotals && totalsPosition === 'top' && <GrandTotalRow />}
        </thead>
        <tbody>
          {useVirtual && topPad > 0 && <tr style={{ height: topPad }}><td colSpan={displayKeys.length + (cfg.rowNumbers ? 1 : 0)} /></tr>}
          {visibleRows.map((item, ri) => {
            const actualRi = startIdx + ri;
            if (item.type === 'group_header') {
              const isCollapsed = collapsedGroups.has(item.groupKey);
              const toggle = () => setCollapsedGroups(prev => {
                const next = new Set(prev);
                if (next.has(item.groupKey)) next.delete(item.groupKey); else next.add(item.groupKey);
                return next;
              });
              const ToggleLabel = (
                <button onClick={toggle} className="flex items-center gap-2 w-full text-left group/gh" data-testid={`group-header-${item.groupKey}`}>
                  <span className="text-[10px] text-muted-foreground transition-transform" style={{ display: 'inline-block', transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}>&#9660;</span>
                  <span className="text-xs font-bold text-foreground">
                    {item.groupLabels.map((label, gi) => (
                      <span key={gi}>{gi > 0 && <span className="text-muted-foreground/70 mx-1">/</span>}{label}</span>
                    ))}
                  </span>
                  <span className="text-[9px] text-muted-foreground/70 ml-1">({item.count} rows)</span>
                </button>
              );
              // When collapsed, surface the group subtotals inline in the header
              // (per-column) so the figures stay visible without expanding.
              if (isCollapsed) {
                const footerAgg = cfg.groupAggregation || 'sum';
                const totals = subtotalMap[item.groupKey] || {};
                return (
                  <tr key={'gh_' + item.groupKey} className="border-b border-border/40 bg-muted/30" style={item.pageBreak && printMode ? { pageBreakBefore: 'always' } : {}}>
                    {cfg.rowNumbers && <td className={cellPad}></td>}
                    {displayKeys.map((k, ki) => {
                      if (ki === 0) return <td key={k} className={cellPad + ' py-2'}>{ToggleLabel}</td>;
                      if (numericCols.includes(k)) {
                        const val = footerAgg === 'avg' ? (totals[`${k}_avg`] || 0) : footerAgg === 'count' ? (totals._count || 0) : (totals[k] || 0);
                        return <td key={k} className={cellPad + ' font-mono text-primary tabular-nums text-xs font-bold'} style={{ textAlign: getColAlign(k) }} data-testid="collapsed-subtotal">{formatValue(val, k)}</td>;
                      }
                      return <td key={k} className={cellPad}></td>;
                    })}
                  </tr>
                );
              }
              return (
                <tr key={'gh_' + item.groupKey} className="border-b border-border/40 bg-muted/30" style={item.pageBreak && printMode ? { pageBreakBefore: 'always' } : {}}>
                  <td colSpan={displayKeys.length + (cfg.rowNumbers ? 1 : 0)} className={cellPad + ' py-2'}>
                    {ToggleLabel}
                  </td>
                </tr>
              );
            }
            if (item.type === 'group_footer') {
              if (collapsedGroups.has(item.groupKey)) return null;
              const footerAgg = cfg.groupAggregation || 'sum';
              return (
                <tr key={'gf_' + item.groupKey} className="border-b-2 border-border/40 bg-primary/5 font-semibold">
                  {cfg.rowNumbers && <td className={cellPad}></td>}
                  {displayKeys.map((k, ki) => {
                    if (ki === 0) return <td key={k} className={cellPad + ' font-mono text-primary text-xs font-bold'} style={{ textAlign: getColAlign(k) }}>{subtotalLabelFor((item.groupLabels || []).join(' / '))}</td>;
                    if (numericCols.includes(k)) {
                      const val = footerAgg === 'avg' ? (item.totals[`${k}_avg`] || 0) : footerAgg === 'count' ? (item.totals._count || 0) : (item.totals[k] || 0);
                      return <td key={k} className={cellPad + ' font-mono text-primary tabular-nums'} style={{ textAlign: getColAlign(k) }}>{formatValue(val, k)}</td>;
                    }
                    return <td key={k} className={cellPad}></td>;
                  })}
                </tr>
              );
            }
            if (item.type === 'subtotal') {
              return (
                <tr key={'sub_' + ri} className="border-b border-border/40 bg-primary/5 font-semibold">
                  {cfg.rowNumbers && <td className={cellPad + ' text-muted-foreground'}></td>}
                  {displayKeys.map((k, ki) => (
                    <td key={k} className={cellPad + ' font-mono text-foreground'} style={{ textAlign: getColAlign(k) }}>
                      {ki === 0 ? (/\{group\}/.test(cfg.subtotalLabel ?? 'Subtotal') ? subtotalLabelFor(item.group) : `${subtotalLabelFor(item.group)}: ${item.group}`) : numericCols.includes(k) ? formatValue(item.totals[k] || 0, k) : ''}
                    </td>
                  ))}
                </tr>
              );
            }
            const { row, index: i } = item;
            const isForecastRow = row._isForecast === true;
            const isMutedRow = row._muted === true;
            const isAnomalyRow = !isMutedRow && (row._anomaly === 'high' || row._anomaly === 'low');
            const anomalyTitle = isAnomalyRow ? `Outlier — this row's value is ${row._anomaly === 'high' ? 'well above' : 'well below'} the typical range for the other rows` : undefined;
            return (
              <tr key={i} title={isMutedRow ? 'No denominator (e.g. missing premium) — ratio is not meaningful for this row' : anomalyTitle} className={'border-b border-border/20 transition-colors cursor-pointer ' + (isForecastRow ? 'bg-orange-500/10 ' : (isAnomalyRow ? 'bg-amber-500/10 border-l-2 border-l-amber-500 ' : (cfg.striped !== false && actualRi % 2 === 1 ? 'bg-[#f0f0f0] dark:bg-[#1a1a2e] ' : ''))) + (isMutedRow ? 'opacity-40 italic ' : '') + 'hover:bg-[#e5e5e5] dark:hover:bg-[#252540]'} style={printMode ? { pageBreakInside: 'avoid' } : { height: useVirtual ? ROW_HEIGHT : undefined }} onClick={() => !isForecastRow && row[rowDims[0]] && onDrill(row[rowDims[0]])} onContextMenu={(onVizConfigChange || onRowContextMenu) ? (e) => { if (onVizConfigChange && !onRowContextMenu) { e.preventDefault(); e.stopPropagation(); setHeaderMenu(null); setRowMenu({ x: e.clientX, y: e.clientY, flipY: e.clientY }); } else if (onRowContextMenu) { e.preventDefault(); onRowContextMenu(row, e); } } : undefined}>
                {cfg.rowNumbers && <td className={cellPad + ' text-muted-foreground/70 font-mono'}>{isForecastRow ? '' : i + 1}</td>}
                {displayKeys.map((k, ki) => {
                  if (k === SPARK_KEY) {
                    // Either an explicit per-row time series (rowTrends, keyed
                    // by the first row dimension) or, as a fallback, the numeric
                    // columns of THIS row. Tiny SVG so it fits in a row.
                    const rawSeries = hasRowTrends
                      ? (rowTrends[String(row[rowDims[0]])] || [])
                      : numericCols.map(nc => +row[nc]);
                    const sparkLabels = hasRowTrends
                      ? (Array.isArray(rowTrendLabels) ? rowTrendLabels : [])
                      : numericCols;
                    const pts = rawSeries
                      .map(v => (v === null || v === undefined ? NaN : +v))
                      .filter(v => Number.isFinite(v));
                    if (pts.length < 2) return <td key={k} className={cellPad} style={{ textAlign: 'center' }} data-testid="row-sparkline"><span className="text-muted-foreground/30 text-[9px]">—</span></td>;
                    const sparkTip = rawSeries.map((v, idx) => {
                      const lbl = sparkLabels[idx] != null ? sparkLabels[idx] : `#${idx + 1}`;
                      const ok = v !== null && v !== undefined && Number.isFinite(+v);
                      const val = !ok ? '—' : (rowTrendTarget ? formatValue(+v, rowTrendTarget) : (+v).toLocaleString());
                      return `${lbl}: ${val}`;
                    }).join('\n');
                    const min = Math.min(...pts);
                    const max = Math.max(...pts);
                    const range = max - min || 1;
                    const W = 80, H = 18;
                    const stepX = W / (pts.length - 1);
                    const path = pts.map((v, idx) => {
                      const x = idx * stepX;
                      const y = H - ((v - min) / range) * H;
                      return `${idx === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
                    }).join(' ');
                    const last = pts[pts.length - 1];
                    const lastY = H - ((last - min) / range) * H;
                    const trendUp = pts[pts.length - 1] >= pts[0];
                    const stroke = trendUp ? '#10b981' : '#ef4444';
                    return (
                      <td key={k} className={cellPad} style={{ textAlign: 'center' }} data-testid="row-sparkline">
                        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: 'inline-block', verticalAlign: 'middle', cursor: 'help' }}>
                          <title>{sparkTip}</title>
                          <path d={path} fill="none" stroke={stroke} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                          <circle cx={W} cy={lastY} r={2} fill={stroke} />
                        </svg>
                      </td>
                    );
                  }
                  const cf = condFormats[k] ? evalCondFormat(row[k], condFormats[k], colValues[k] || []) : {};
                  const cellStyle = { textAlign: getColAlign(k), ...cf.style };
                  if (cf.style?._barColor) delete cellStyle._barColor;
                  if (cf.style?._iconColor) delete cellStyle._iconColor;
                  if (item.mergeBlank && item.mergeBlank[k]) {
                    // Merged (repeated) dimension cell — render empty to mimic Excel.
                    return <td key={k} className={cellPad + ' font-mono'} style={cellStyle} data-testid="merged-cell" />;
                  }
                  return (
                    <td key={k} className={cellPad + ' font-mono relative ' + wrapClass + ' ' + (isForecastRow ? 'text-orange-400 italic' : '')} style={cellStyle}>
                      {cf.barWidth !== null && cf.barWidth !== undefined && (
                        <div className="absolute inset-y-0 left-0 rounded-r" style={{ width: `${cf.barWidth}%`, backgroundColor: cf.style?._barColor || 'rgba(59,130,246,0.2)' }} />
                      )}
                      <span className="relative z-10" style={!isForecastRow && !cf.style?.color ? { color: 'var(--foreground)', opacity: 0.8 } : undefined}>
                        {ki === 0 && isAnomalyRow && <span onClick={(e) => { e.stopPropagation(); onAnomalyClick && onAnomalyClick(row); }} title={onAnomalyClick ? 'Why is this an outlier?' : anomalyTitle} role={onAnomalyClick ? 'button' : undefined} style={{ color: '#f59e0b', marginRight: 4, fontSize: '0.85em', cursor: onAnomalyClick ? 'pointer' : 'default' }} data-testid="anomaly-flag">{'\u26A0'}</span>}
                        {cf.icon && <span style={{ color: cf.style?._iconColor, marginRight: 4, fontSize: '0.7em' }}>{cf.icon}</span>}
                        {formatValue(row[k], k)}
                      </span>
                      {typeof row[k + '__prior_pct'] === 'number' && (
                        <span
                          className="relative z-10 block text-[9px] font-sans"
                          style={{ color: row[k + '__prior_pct'] >= 0 ? '#10b981' : '#ef4444', opacity: 0.9 }}
                          title={`Prior year: ${formatValue(row[k + '__prior'], k)} — vs same point last year`}
                          data-testid="table-prior-delta"
                        >
                          {row[k + '__prior_pct'] >= 0 ? '\u25B2' : '\u25BC'} {Math.abs(row[k + '__prior_pct'])}% vs LY
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
          {useVirtual && bottomPad > 0 && <tr style={{ height: bottomPad }}><td colSpan={displayKeys.length + (cfg.rowNumbers ? 1 : 0)} /></tr>}
        </tbody>
        {cfg.grandTotals && totalsPosition === 'bottom' && (
          <tfoot style={printMode ? { display: 'table-footer-group' } : {}}>
            <GrandTotalRow />
          </tfoot>
        )}
      </table>
    </div>
    {totalRows > data.length && (
      <p className="text-[10px] text-amber-500 px-3 py-1 bg-amber-500/5 border-t border-amber-500/20">
        Showing {data.length.toLocaleString()} of {totalRows.toLocaleString()} rows. Increase "Max Rows" in table config to see more.
      </p>
    )}
    <p className="text-[9px] text-muted-foreground px-3 py-1">{data.length.toLocaleString()} rows displayed</p>
  </div>
  );
});
